import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyS, HeadlineXL } from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { DialogKey } from '#src/components/navigation/WithDialogs/index.tsx';
import { OldConversionCredit } from '#src/components/wallet/BankEthAccounting/ConversionCredits/OldConversionCredit/index.tsx';
import { ANY_SPORT_MARKET_SHOP_INSTANT_BUY } from '#src/constants/__generated__/routes.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useWalletDrawerContext } from '#src/contexts/walletDrawer/index.tsx';
import { idFromObject } from '#src/gql/idFromObject.ts';
import { useCloseDialog } from '#src/hooks/navigation/useCloseDialog.ts';
import { useDialogParam } from '#src/hooks/navigation/useDialogParam.ts';
import { useAmountWithConversion } from '#src/hooks/useAmountWithConversion.ts';
import { useConversionCredits } from '#src/hooks/useConversionCredits.tsx';
import { useCurrentUserCurrencies } from '#src/hooks/useCurrentUserCurrencies.tsx';
import { glossary } from '#src/lib/glossary.ts';
import { MonetaryAmount } from '#src/lib/monetaryAmount.ts';
import { generateSportPath } from '#src/lib/routing/generateSportPath.ts';
import { getFormattedSport } from '#src/lib/sports.ts';

const Root = styled(Vertical).attrs({ gap: 2, center: true })`
  padding: 0 var(--triple-unit) var(--triple-unit);
  text-align: center;
  justify-content: flex-start;
`;

const messages = defineMessages({
  addedToWallet: {
    defaultMessage: 'This credit has been added to your Wallet.',
  },
  description: {
    defaultMessage:
      'You’ll get a {percentage} discount up to {amount} on all purchases.',
  },
  descriptionWithSport: {
    defaultMessage:
      'You’ll get a {percentage} discount up to {amount} on all purchases in the {sport} Market.',
  },
});

const Footer = styled(Horizontal)`
  width: 100%;
`;

const StyledConversionCredit = styled(OldConversionCredit)`
  border: 1px solid var(--c-nd-200);
  border-radius: var(--unit);
  padding: var(--double-unit);
`;

export const CreditClaimedDialog = () => {
  const { fiatCurrency } = useCurrentUserCurrencies();
  const { formatNumber } = useIntl();
  const { sport: sportContext } = useSportContext();
  const { toggleDrawer } = useWalletDrawerContext();
  const { conversionCredits } = useConversionCredits({});
  const closeDialog = useCloseDialog();
  const id = useDialogParam(DialogKey.claimedConversionCredit);

  const claimedConversionCredit = conversionCredits?.find(
    cc => idFromObject(cc.id) === id
  );

  const { main } = useAmountWithConversion({
    monetaryAmount: claimedConversionCredit?.maxDiscount || MonetaryAmount.zero,
  });

  if (!claimedConversionCredit) return null;

  const percentage = formatNumber(claimedConversionCredit.percentageDiscount, {
    style: 'percent',
    maximumFractionDigits: 0,
  });

  return (
    <Dialog
      open
      onClose={() => {
        closeDialog(DialogKey.claimedConversionCredit);
      }}
      maxWidth="xs"
      fullWidth
    >
      <Root>
        <HeadlineXL as="h2">
          <FormattedMessage
            defaultMessage="Congratulations!{br}You won a {percentage} discount."
            values={{ percentage, br: <br /> }}
          />
        </HeadlineXL>
        <BodyS color="var(--c-nd-600)" as="p">
          <FormattedMessage
            {...(claimedConversionCredit.sport
              ? messages.descriptionWithSport
              : messages.description)}
            values={{
              percentage,
              amount: main,
              sport: claimedConversionCredit.sport
                ? getFormattedSport(claimedConversionCredit.sport)
                : '',
            }}
          />
        </BodyS>
        <StyledConversionCredit
          referenceCurrency={fiatCurrency}
          conversionCredit={claimedConversionCredit}
        />
        <Footer grow>
          <Button
            onClick={() => {
              toggleDrawer();
              closeDialog(DialogKey.claimedConversionCredit);
            }}
            fullWidth
            color="tertiary"
            size="small"
          >
            <FormattedMessage defaultMessage="See wallet" />
          </Button>
          <Button
            to={generateSportPath(ANY_SPORT_MARKET_SHOP_INSTANT_BUY, {
              sport: claimedConversionCredit.sport || sportContext,
            })}
            fullWidth
            size="small"
          >
            <FormattedMessage {...glossary.goToMarket} />
          </Button>
        </Footer>
      </Root>
    </Dialog>
  );
};
